import { i18nNsFactory } from '@atlas-frontend/i18n';
import { useMemo } from 'react';
import { formatDate, getDatePeriodString } from '../../common/utils';
import { ExecutionPublicationFormDataType } from '../schema';
import { Table } from '../../common/tanstacktable';
import { createColumnHelper } from '@tanstack/react-table';
import { TextEllipsis } from '../../common/components';
import translations from '../translations/CalendarView.translations.json';
import Checkbox from 'antd/es/checkbox';
import { Flex } from 'antd';
import { useShowPastYear } from './useShowPastYear';

const { addTranslations, useTranslation } = i18nNsFactory(
  'details-calendar-table'
);
addTranslations(translations);

interface ExecutionPublicationsProps {
  publications: ExecutionPublicationFormDataType[];
}

const columnHelper = createColumnHelper<ExecutionPublicationFormDataType>();

export function ExecutionPublicationsViewTable({
  publications,
}: ExecutionPublicationsProps) {
  const { t } = useTranslation();
  const {
    showPastYear,
    onChange: onChangeShowPastYear,
    hideOldRows,
  } = useShowPastYear();

  const columns = useMemo(
    () => [
      columnHelper.accessor((rowData) => formatDate(rowData.executionDate), {
        id: 'executionDate',
        header: t('column.executionDate.label'),
        meta: { width: 150 },
        sortingFn: 'formattedDate',
        enableColumnFilter: false,
      }),
      columnHelper.accessor('validPeriod', {
        header: t('column.validPeriod.label'),
        enableColumnFilter: false,
        enableSorting: false,
        cell: (props) => (
          <TextEllipsis>
            {getDatePeriodString(
              props.row.original.validPeriod[0],
              props.row.original.validPeriod[1]
            )}
          </TextEllipsis>
        ),
      }),
    ],
    [t]
  );

  return (
    <Flex vertical gap={12}>
      <Flex gap="small" align="center">
        <Checkbox
          id="show-past-year"
          checked={showPastYear}
          onChange={onChangeShowPastYear}
        >
          {t('checkbox.show-past-year')}
        </Checkbox>
      </Flex>
      <Table
        name="execution.executionPublications"
        data={publications}
        columns={columns}
        rowKey="id"
        sticky
        virtual
        scrollContainerSelector="#detail-layout-main-content"
        defaultSort={[{ id: 'executionDate', desc: true }]}
        rowHtmlAttributes={hideOldRows}
      />
    </Flex>
  );
}
